// Fire family render (owner 2026-11-02): Fire Breath, Wyrmstorm, Flame Ring / Sunfire Crown, burning foes and fire pops.
// Render-only: reads sim.breath / sim.enemies (burnT) and the 'breath' / 'breathEnd' / 'flameRing' / 'flameLinger' / 'wyrm' /
// 'wyrmBurst' / 'firePop' events; it never decides gameplay.
//  - Fire Breath: a cel / anime flame cone from the hero's MOUTH (found on the authored model's head bone; the procedural
//    rig's head as a fallback): three nested cone sheets (ragged red-orange outer flame, amber body, white-yellow core near
//    the mouth) with noise scrolling down the jet and hard colour bands, plus flame puffs, embers and smoke along it, a warm
//    ground glow and char under the flame. The head turns toward the jet while running; standing, the body squares up (view.ts).
//  - burning foes: one or two cel flame cards on every burning foe (all foes share ONE instanced mesh with the ring pillars
//    and the wyrm: one draw call however many burn), a few embers and a small ember glow on the ground under the nearest.
//  - Flame Ring: a ring of flame pillars rolling out from the hero, scorching the ground as it passes (ground.stampBurn)
//    with a hot ground glow at its front; rank 5 / Sunfire Crown leaves a ring of low flames burning where it stopped.
//  - Wyrmstorm: a serpent of fire (a head with horns and eye glints, a tapering body of flame cards on a sinuous arc) leaves
//    the mouth, rises and dives to the end of the cone, then erupts: flash (FLASH-limited), shock rings, a crown of flame
//    pillars bursting outward and a wide scorch.
// Allocation-free per frame; effect counts scale with the particle pool (Particle density setting).
import * as THREE from 'three/webgpu';
import { abs, color, cos, float, instancedBufferAttribute, max, mix, mx_noise_float, pow, sin, smoothstep, time, uniform, uv, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import type { GltfHero } from './heroGltf';
import type { HeroRig } from './models';

/** cel flame bands (dark ink edge → red → orange → amber → white-yellow), shared by the cone and the cards */
const INK = 0x4a0804, RED = 0xd8300c, ORANGE = 0xff6a14, AMBER = 0xffa526, HOT = 0xffe07a, WHITE = 0xfff6cf;
const RING_PILLAR_GAP = 0.85, LINGER_GAP = 1.15, WYRM_SEGS = 40, WYRM_STEP = 0.02;

interface RingFx { x: number; z: number; r: number; speed: number; t: number; evo: boolean; stamped: number; id: number; burst: boolean }
interface LingerFx { x: number; z: number; r: number; w: number; t: number; dur: number; evo: boolean }
interface WyrmFx { x0: number; y0: number; z0: number; x1: number; z1: number; t: number; dur: number; r: number; side: number; burst: boolean }

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
const _v = new THREE.Vector3(), _a = new THREE.Vector3(), _f = new THREE.Vector3(), _r = new THREE.Vector3(), _u = new THREE.Vector3();
const _pq = new THREE.Quaternion(), _up = new THREE.Vector3(0, 1, 0), _z = new THREE.Vector3(0, 0, 1);
const hash = (j: number): number => { const h = Math.sin(j * 91.345 + 7.13) * 43758.5453; return h - Math.floor(h); };

/** the cone: rings along the jet (u 0 at the mouth → 1 at the end), local +z = along, x/y = the cross-section */
function coneGeometry(LS = 16, RS = 22): THREE.BufferGeometry {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  for (let i = 0; i <= LS; i++) {
    const u = i / LS, r = 0.06 + 0.94 * Math.pow(u, 0.75);
    for (let j = 0; j <= RS; j++) { const a = (j / RS) * Math.PI * 2; pos.push(Math.cos(a) * r, Math.sin(a) * r, u); uvs.push(u, j / RS); }
  }
  for (let i = 0; i < LS; i++) for (let j = 0; j < RS; j++) { const a = i * (RS + 1) + j, b = a + RS + 1; idx.push(a, b, a + 1, a + 1, b, b + 1); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2)); g.setIndex(idx);
  return g;
}

export class FlameSkillsFx {
  /** 0..1 how strongly the hero is breathing fire (view.ts squares the standing hero up to aimYaw while > 0.3) */
  pose = 0; aimYaw = 0;
  /** QA counters */
  casts = 0; rings = 0; wyrmsSeen = 0; pops = 0; burningDrawn = 0; mouthFound = false;
  /** the mouth this frame (world) */
  readonly mouth = new THREE.Vector3();
  private cards: THREE.InstancedMesh; private cardAttr: THREE.InstancedBufferAttribute; private cap: number;
  private cones: THREE.Mesh[] = []; private coneK = uniform(0); private coneHot = uniform(0);
  /** rank 5 / Wyrmstorm see-through (owner 2026-11-02: 'so opaque it hides the foes inside'): 0..1, eased */
  private coneSee = uniform(0);
  private on = 0; private grow = 0; private evo = false; private active = false; private range = 5; private half = 0.2;
  private dir = new THREE.Vector3(1, 0, 0); private endP = new THREE.Vector3();
  private ringFx: RingFx[] = []; private lingerFx: LingerFx[] = []; private wyrmFx: WyrmFx[] = [];
  private puffAcc = 0; private emberAcc = 0; private charAcc = 0; private t = 0;
  private mouthLocal: THREE.Vector3 | null = null; private headBone: THREE.Bone | null = null; private neckBone: THREE.Bone | null = null; private mouthHero: GltfHero | null = null;
  private twistBase = [new THREE.Quaternion(), new THREE.Quaternion()]; private twistOut = [new THREE.Quaternion(), new THREE.Quaternion()]; private twisted = false;

  constructor(scene: THREE.Scene, private fx: Fx, private glow: GroundGlow, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void, quality = 'high') {
    // ---- flame cards (burning foes, ring pillars, lingering circle, wyrm body): the MAP-FIRE cel tongue, one instanced mesh
    this.cap = quality === 'low' ? 420 : 900;
    this.cardAttr = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 2), 2); this.cardAttr.setUsage(THREE.DynamicDrawUsage);
    const g = new THREE.PlaneGeometry(1, 1, 1, 1).translate(0, 0.5, 0); // pivot at the base
    const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, alphaTest: 0.5, fog: false });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const F = instancedBufferAttribute(this.cardAttr) as any;
    const seed = F.x, heat = F.y;
    const u = uv(), x = u.x.sub(0.5).mul(2), y = u.y;
    const w0 = pow(float(1).sub(y), 0.7).mul(smoothstep(0.0, 0.18, y).mul(0.4).add(0.6)).mul(0.95);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const n = (mx_noise_float(vec3(x.mul(1.7), y.mul(2.4).sub(time.mul(3.1)), seed.mul(17))) as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const n2 = (mx_noise_float(vec3(x.mul(3.6), y.mul(5).sub(time.mul(5)), seed.mul(5).add(3))) as any);
    const sway = sin(y.mul(3.2).add(time.mul(6.5)).add(seed.mul(20))).mul(0.14).mul(y);
    const tongues = cos(x.mul(Math.PI * 1.7).add(seed.mul(6)).add(n.mul(2.2)).add(time.mul(1.6))).mul(0.5).add(0.5);
    const w = w0.mul(mix(float(1), tongues.mul(0.95).add(0.05), smoothstep(0.12, 0.6, y)));
    const shape = w.add(n.mul(0.3).mul(y.add(0.2))).add(n2.mul(0.1)).sub(abs(x.add(sway)).mul(0.85));
    const k = shape.div(max(w0, 0.08)).add(float(1).sub(y).mul(0.25)).add(heat.mul(0.22));
    let col = mix(color(INK), color(RED), smoothstep(0.06, 0.1, k));
    col = mix(col, color(ORANGE), smoothstep(0.3, 0.34, k));
    col = mix(col, color(AMBER), smoothstep(0.52, 0.56, k));
    col = mix(col, color(HOT), smoothstep(0.76, 0.8, k).mul(smoothstep(0.85, 0.45, y)));
    m.colorNode = col.mul(heat.mul(0.35).add(0.8));
    m.opacityNode = smoothstep(-0.01, 0.01, shape);
    this.cards = new THREE.InstancedMesh(g, m, this.cap);
    this.cards.frustumCulled = false; this.cards.count = 0; this.cards.renderOrder = 3;
    scene.add(this.cards);

    // ---- the breath cone: three nested sheets
    const geo = coneGeometry();
    const layer = (hot: number, speed: number, additive: boolean, ink: boolean) => {
      const mm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
      const cu = uv().x, a = uv().y.mul(Math.PI * 2), ca = cos(a), sa = sin(a);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nn = (mx_noise_float(vec3(cu.mul(4.2).sub(time.mul(speed)), ca.mul(1.3), sa.mul(1.3).add(hot * 7))) as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nf = (mx_noise_float(vec3(cu.mul(10).sub(time.mul(speed * 1.7)), ca.mul(2.6).add(4), sa.mul(2.6))) as any);
      const d = float(1).sub(cu.mul(0.92)).add(nn.mul(0.42)).add(nf.mul(0.17)).add(hot).add(this.coneHot);
      let c = ink ? mix(color(INK), color(RED), smoothstep(0.1, 0.14, d)) : color(RED);
      c = mix(c, color(ORANGE), smoothstep(0.34, 0.38, d));
      c = mix(c, color(AMBER), smoothstep(0.56, 0.6, d));
      c = mix(c, color(HOT), smoothstep(0.78, 0.82, d));
      c = mix(c, color(WHITE), smoothstep(1.12, 1.16, d));
      mm.colorNode = additive ? c.mul(this.coneK.mul(0.3)) : c;
      const ragged = smoothstep(1.0, 0.82, cu.add(nn.mul(0.22)).add(nf.mul(0.08)));
      // see-through middle (rank 5 / Wyrmstorm): the faces of the jet the overhead camera looks through (top and bottom of the
      // cross-section, |sin a| → 1) thin out between the mouth and the ragged tip; the side walls (the cone's silhouette
      // edges from above), the mouth and the tips keep their full, bright bands
      const midU = smoothstep(0.06, 0.24, cu).mul(smoothstep(0.97, 0.78, cu.add(nn.mul(0.1))));
      const see = float(1).sub(this.coneSee.mul(0.74).mul(pow(abs(sa), 1.6)).mul(midU));
      mm.opacityNode = additive ? float(1) : smoothstep(0.1, 0.13, d).mul(smoothstep(0.0, 0.06, cu)).mul(ragged).mul(this.coneK).mul(see);
      const mesh = new THREE.Mesh(geo, mm);
      mesh.frustumCulled = false; mesh.matrixAutoUpdate = false; mesh.visible = false; mesh.renderOrder = additive ? 8 : 7;
      scene.add(mesh); this.cones.push(mesh);
    };
    layer(-0.05, 7, false, true);  // outer flame: ragged red-orange tongues with an ink edge
    layer(0.18, 9, false, false);  // body: orange / amber
    layer(0.42, 12, true, false);  // core: white-yellow glow near the mouth (additive, dimmed)
  }

  // ------------------------------------------------------------------------------------------------------------------
  event(ev: SimEvent, sim: Sim): void {
    switch (ev.t) {
      case 'breath': this.casts++; this.active = true; this.evo = ev.evolved; this.range = ev.range; this.half = ev.half; this.grow = 0; this.aimYaw = Math.atan2(ev.dx, ev.dz);
        if (ev.evolved && FLASH.dim >= 0.5) this.fx.lights.flash(ev.x, 2, ev.z, 0xff8a2a, 22 * FLASH.dim, 0.25, 12);
        break;
      case 'breathEnd': this.active = false; break;
      case 'flameRing': {
        this.rings++;
        this.ringFx.push({ x: ev.x, z: ev.z, r: ev.r, speed: ev.speed, t: 0, evo: ev.evolved, stamped: 0, id: ev.id, burst: false });
        const gy = this.groundAt(ev.x, ev.z);
        this.fx.rings.add(ev.x, gy + 0.12, ev.z, Math.min(ev.r, 4), 0.35, 0xff7a24, 1.1, 2);
        this.fx.particles.emit(ev.x, gy + 0.6, ev.z, ev.evolved ? 26 : 16, 7, 0xffa526, 0.35, 0.5, 3, -2, 2.5, 3);
        this.glow.flash(ev.x, ev.z, 3.2, 0xff7a24, 1.2, 0.3);
        if (ev.k === 0) this.fx.lights.flash(ev.x, gy + 1.2, ev.z, 0xff7a24, ev.evolved ? 26 : 14, 0.25, 10);
        break;
      }
      case 'flameLinger': this.lingerFx.push({ x: ev.x, z: ev.z, r: ev.r, w: ev.w, t: 0, dur: ev.dur, evo: ev.evolved }); break;
      case 'wyrm': {
        this.wyrmsSeen++;
        const M = this.mouth.lengthSq() > 0 ? this.mouth : _v.set(ev.x0, this.groundAt(ev.x0, ev.z0) + 1.7, ev.z0);
        this.wyrmFx.push({ x0: M.x, y0: M.y, z0: M.z, x1: ev.x1, z1: ev.z1, t: 0, dur: ev.dur, r: ev.r, side: this.wyrmFx.length % 2 ? -1 : 1, burst: false });
        break;
      }
      case 'wyrmBurst': {
        const gy = this.groundAt(ev.x, ev.z);
        this.fx.explosion(ev.x, gy + 0.8, ev.z, ev.r * 0.55, 0xff8a2a, true); // big: light / stars go through the FLASH budget
        this.fx.rings.add(ev.x, gy + 0.15, ev.z, ev.r * 1.5, 0.7, 0xff5a14, 1, 3);
        this.ringFx.push({ x: ev.x, z: ev.z, r: ev.r * 1.1, speed: ev.r * 3.2, t: 0, evo: true, stamped: 0, id: 9000 + ev.id, burst: true }); // a crown of pillars bursting outward
        this.burn(ev.x, ev.z, ev.r * 0.8);
        for (let k = 0; k < 8; k++) { const a = k * Math.PI / 4 + 0.3; this.burn(ev.x + Math.cos(a) * ev.r * 0.75, ev.z + Math.sin(a) * ev.r * 0.75, ev.r * 0.35); }
        this.glow.flash(ev.x, ev.z, ev.r * 1.6, 0xff7a20, 1.6, 0.55);
        this.fx.shake = Math.min(1, this.fx.shake + 0.25);
        break;
      }
      case 'firePop': {
        this.pops++;
        const gy = this.groundAt(ev.x, ev.z);
        this.fx.stars.add(ev.x, gy + 1.0, ev.z, ev.r * 0.9, 0.16, 0xff8a2a, 1.2);
        this.fx.particles.emit(ev.x, gy + 0.9, ev.z, 10, 5, 0xffa526, 0.3, 0.45, 2.5, -2, 2.5, 2.6);
        this.fx.rings.add(ev.x, gy + 0.12, ev.z, ev.r, 0.3, 0xff6a14, 0.9, 2);
        this.glow.flash(ev.x, ev.z, ev.r * 1.2, 0xff6a14, 0.9, 0.25);
        this.burn(ev.x, ev.z, ev.r * 0.6);
        break;
      }
    }
    void sim;
  }

  // ------------------------------------------------------------------------------------------------------------------
  /** Per frame, after the hero is posed. hx/hz = drawn hero position; alpha = sim interpolation. */
  update(dt: number, sim: Sim, cam: THREE.Camera, rig: HeroRig, gltf: GltfHero | null, hx: number, hz: number, alpha: number, moving: boolean): void {
    this.t += dt;
    const b = sim.breath, density = Math.min(1.5, this.fx.particles.capacity / 2200);
    if (!b.active && this.active) this.active = false;
    this.on += ((b.active ? 1 : 0) - this.on) * Math.min(1, dt * (b.active ? 22 : 9));
    this.pose += ((b.active ? 1 : 0) - this.pose) * Math.min(1, dt * (b.active ? 16 : 6));
    if (b.active) { this.grow = Math.min(1, this.grow + dt / 0.14); this.range = b.range; this.half = b.half; this.evo = b.evo; this.aimYaw = Math.atan2(b.ax, b.az); }
    this.headTwist(gltf, moving && this.pose > 0.05 ? this.pose : 0);
    // mouth
    if (this.on > 0.01 || b.active) {
      if (gltf && gltf.ready && gltf.root.visible) {
        if (this.mouthHero !== gltf) this.mouthFound = this.findMouth(gltf);
        if (this.mouthLocal && this.headBone) { gltf.root.updateMatrixWorld(true); this.headBone.localToWorld(this.mouth.copy(this.mouthLocal)); }
        else this.rigMouth(rig);
      } else this.rigMouth(rig);
    }
    // ---- the cone
    if (this.on > 0.02) {
      const ix = b.pdx + (b.dx - b.pdx) * alpha, iz = b.pdz + (b.dz - b.pdz) * alpha, il = Math.hypot(ix, iz) || 1;
      const L0 = this.range * (0.25 + 0.75 * this.grow), ex = hx + (ix / il) * L0, ez = hz + (iz / il) * L0;
      this.endP.set(ex, this.groundAt(ex, ez) + 0.5, ez);
      _f.subVectors(this.endP, this.mouth); const L = _f.length() || 1; _f.divideScalar(L);
      this.dir.copy(_f);
      _r.crossVectors(_up, _f).normalize(); _u.crossVectors(_f, _r).normalize();
      const R = Math.tan(this.half) * L * 1.08 * (0.6 + 0.4 * this.grow) * (0.85 + 0.15 * this.on), flat = 0.55;
      _m.makeBasis(_r.multiplyScalar(R), _u.multiplyScalar(R * flat), _a.copy(_f).multiplyScalar(L)).setPosition(this.mouth);
      for (let i = 0; i < 3; i++) {
        const c = this.cones[i];
        if (i === 2) { _s.set(0.5, 0.5, 0.55); _q.identity(); _m2.compose(_v.set(0, 0, 0), _q, _s); c.matrix.multiplyMatrices(_m, _m2); }
        else if (i === 1) { _s.set(0.78, 0.78, 0.92); _q.identity(); _m2.compose(_v.set(0, 0, 0), _q, _s); c.matrix.multiplyMatrices(_m, _m2); }
        else c.matrix.copy(_m);
        c.matrixWorldNeedsUpdate = true; c.visible = true;
      }
      this.coneK.value = Math.min(1, this.on * 1.15) * (FLASH.dim < 1 ? 0.85 + 0.15 * FLASH.dim : 1);
      this.coneHot.value = this.evo ? 0.08 : 0;
      this.coneSee.value += ((b.lvl >= 5 || this.evo ? 1 : 0) - this.coneSee.value) * Math.min(1, dt * 8);
      // flame puffs riding the jet, embers spilling off it, smoke at the end, char under it
      if (b.active) {
        const sp = 13, life = L / sp, spread = Math.tan(this.half);
        this.puffAcc += dt * (this.evo ? 120 : 75) * density;
        for (; this.puffAcc >= 1; this.puffAcc--) {
          const s1 = (Math.random() * 2 - 1) * spread * 0.8, s2 = (Math.random() * 2 - 1) * spread * 0.4, v = sp * (0.85 + Math.random() * 0.3);
          const side = _rr(s1, _f), vx = (_f.x + side.x) * v, vz = (_f.z + side.z) * v, vy = (_f.y + s2) * v;
          const roll = Math.random(), col = roll < 0.3 ? HOT : roll < 0.7 ? AMBER : ORANGE;
          this.fx.particles.emitDir(this.mouth.x + _f.x * 0.25, this.mouth.y + _f.y * 0.25, this.mouth.z + _f.z * 0.25, vx, vy, vz, col, 0.3 + Math.random() * 0.35, life * (0.75 + Math.random() * 0.35), 3);
        }
        this.emberAcc += dt * 28 * density;
        for (; this.emberAcc >= 1; this.emberAcc--) {
          const u2 = 0.3 + Math.random() * 0.7, px = this.mouth.x + (this.endP.x - this.mouth.x) * u2, pz = this.mouth.z + (this.endP.z - this.mouth.z) * u2, py = this.mouth.y + (this.endP.y - this.mouth.y) * u2;
          if (Math.random() < 0.7) this.fx.particles.emitDir(px, py, pz, (Math.random() - 0.5) * 3, 2 + Math.random() * 3, (Math.random() - 0.5) * 3, Math.random() < 0.5 ? 0xff7a20 : 0xffc040, 0.1 + Math.random() * 0.08, 0.6 + Math.random() * 0.5, 4);
          else this.fx.particles.emitDir(this.endP.x, this.endP.y + 0.4, this.endP.z, (Math.random() - 0.5) * 1.2, 1.2 + Math.random(), (Math.random() - 0.5) * 1.2, 0x5a3a30, 0.6, 0.8, 0.5);
        }
        this.charAcc += dt * 14;
        for (; this.charAcc >= 1; this.charAcc--) { const u2 = 0.35 + Math.random() * 0.65, o = (Math.random() * 2 - 1) * spread * L * u2 * 0.6; this.burn(hx + ix / il * L0 * u2 - iz / il * o, hz + iz / il * L0 * u2 + ix / il * o, 0.55); }
        if (FLASH.dim >= 1 && Math.random() < dt * 8) this.fx.lights.flash((this.mouth.x + this.endP.x) / 2, this.mouth.y, (this.mouth.z + this.endP.z) / 2, 0xff7a20, 5, 0.1, 8);
      }
    } else for (const c of this.cones) c.visible = false;

    // ---- flame cards
    const camQ = cam.quaternion, A = this.cardAttr.array as Float32Array;
    _f.set(0, 0, 1).applyQuaternion(camQ); // toward the camera
    let n = 0;
    const put = (x: number, y: number, z: number, w: number, h: number, seed: number, heat: number, q: THREE.Quaternion = camQ) => {
      if (n >= this.cap || h < 0.04) return;
      _p.set(x, y, z); _s.set(w, h, w);
      this.cards.setMatrixAt(n, _m.compose(_p, q, _s)); A[n * 2] = seed; A[n * 2 + 1] = heat; n++;
    };
    // burning foes (sim state; render reads only)
    let burning = 0, emberBudget = Math.round(dt * 40 * density + Math.random());
    for (const e of sim.enemies) {
      if (!e.alive || e.burnT <= 0) continue;
      burning++;
      const rr = e.def.radius * e.scale, gy = this.groundAt(e.x, e.z), fade = Math.min(1, e.burnT / 0.35), base = gy + e.y + e.lift + e.gripY - rr * 0.95;
      const flick = 1 + Math.sin(this.t * 11 + e.id * 1.7) * 0.12, H = rr * 2.4 * fade * flick, W = rr * 1.75 * fade, o = rr * 0.55;
      put(e.x + _f.x * o, base + _f.y * o, e.z + _f.z * o, W, H, (e.id % 97) * 0.37, 0.7);
      if (rr >= 0.9) put(e.x + _f.x * o * 0.6 + rr * 0.4, base + rr * 0.2, e.z + _f.z * o * 0.6, W * 0.7, H * 0.8, (e.id % 89) * 0.53 + 3, 0.5);
      if (emberBudget > 0 && Math.random() < 0.08) { emberBudget--; this.fx.particles.emitDir(e.x + (Math.random() - 0.5) * rr, base + rr * 1.6, e.z + (Math.random() - 0.5) * rr, (Math.random() - 0.5) * 1.2, 1.8 + Math.random() * 1.8, (Math.random() - 0.5) * 1.2, Math.random() < 0.5 ? 0xff7a20 : 0xffc040, 0.1 + Math.random() * 0.07, 0.7 + Math.random() * 0.5, 4); }
    }
    this.burningDrawn = burning;
    // Flame Ring fronts (render clock from the event: the same speed as the sim front) and the bursting wyrm crowns
    for (let i = this.ringFx.length - 1; i >= 0; i--) {
      const R = this.ringFx[i]; R.t += dt;
      const d = Math.min(R.r, 0.6 + R.speed * R.t), done = Math.max(0, R.t - (R.r - 0.6) / R.speed), fadeK = Math.max(0, 1 - done / 0.4);
      if (fadeK <= 0) { this.ringFx.splice(i, 1); continue; }
      const N = Math.min(R.burst ? 34 : 110, Math.max(10, Math.round(2 * Math.PI * d / RING_PILLAR_GAP)));
      const tall = (R.burst ? 2.6 : R.evo ? 2.2 : 1.6) * fadeK * Math.min(1, R.t / 0.08);
      for (let k = 0; k < N; k++) {
        const h1 = hash(k * 7 + R.id), a = (k + h1 * 0.6) / N * Math.PI * 2 + R.id * 0.7, rr = d + (h1 - 0.5) * 0.5, x = R.x + Math.cos(a) * rr, z = R.z + Math.sin(a) * rr;
        const flick = 1 + Math.sin(this.t * (10 + h1 * 6) + k) * 0.14;
        put(x, this.groundAt(x, z) - 0.05, z, (0.75 + h1 * 0.5) * (R.evo ? 1.25 : 1), tall * (0.7 + h1 * 0.6) * flick, h1 * 9 + k, 0.8);
      }
      // scorch the ground as the front passes
      if (d >= R.r * 0.72 && (d - R.stamped >= 0.7 || (d >= R.r && R.stamped < R.r))) { // a scorched RING: only the outer band chars
        R.stamped = d; const M = Math.min(64, Math.max(8, Math.round(2 * Math.PI * d / 1.3)));
        for (let k = 0; k < M; k++) { const a = k / M * Math.PI * 2; this.burn(R.x + Math.cos(a) * d, R.z + Math.sin(a) * d, 0.75); }
      }
      if (Math.random() < dt * 30 * density) { const a = Math.random() * 6.283; this.fx.particles.emitDir(R.x + Math.cos(a) * d, this.groundAt(R.x, R.z) + 0.5, R.z + Math.sin(a) * d, Math.cos(a) * 2, 2 + Math.random() * 2.5, Math.sin(a) * 2, Math.random() < 0.5 ? 0xffa526 : 0xff6a14, 0.14, 0.6, 4); }
    }
    // lingering circles
    for (let i = this.lingerFx.length - 1; i >= 0; i--) {
      const L = this.lingerFx[i]; L.t += dt;
      if (L.t >= L.dur) { this.lingerFx.splice(i, 1); continue; }
      const k0 = Math.min(1, L.t / 0.25) * Math.min(1, (L.dur - L.t) / 0.5), N = Math.min(80, Math.max(10, Math.round(2 * Math.PI * L.r / LINGER_GAP)));
      for (let k = 0; k < N; k++) {
        const h1 = hash(k * 13 + 5), a = (k + h1 * 0.7) / N * Math.PI * 2, rr = L.r + (h1 - 0.5) * L.w * 0.8, x = L.x + Math.cos(a) * rr, z = L.z + Math.sin(a) * rr;
        const flick = 1 + Math.sin(this.t * (8 + h1 * 7) + k * 1.3) * 0.2;
        put(x, this.groundAt(x, z) - 0.05, z, 0.7 + h1 * 0.4, (L.evo ? 1.3 : 1.0) * (0.6 + h1 * 0.6) * k0 * flick, h1 * 11 + k, 0.55);
      }
    }
    // Wyrmstorm: head + horns + tapering body along a rising, sinuous dive
    for (let i = this.wyrmFx.length - 1; i >= 0; i--) {
      const W = this.wyrmFx[i]; W.t += dt;
      const s = W.t / W.dur;
      if (s - WYRM_SEGS * WYRM_STEP > 1) { this.wyrmFx.splice(i, 1); continue; }
      for (let k = WYRM_SEGS; k >= 0; k--) {
        const sk = s - k * WYRM_STEP; if (sk < 0 || sk > 1) continue;
        this.wyrmPoint(W, sk, _p);
        const taper = 1 - k / (WYRM_SEGS + 2), size = (k === 0 ? 3.6 : 0.7 + 2.1 * taper * (0.85 + 0.15 * Math.sin(k * 0.9))) * (W.r / 4.5);
        if (k === 0) {
          put(_p.x, _p.y - size * 0.45, _p.z, size * 0.95, size, 77.7, 1.0);
          for (const sd of [-1, 1]) { _q2.setFromAxisAngle(_z, sd * 0.55); _q.copy(camQ).multiply(_q2); put(_p.x + _r.copy(_v.set(sd * size * 0.28, 0, 0).applyQuaternion(camQ)).x, _p.y + size * 0.15, _p.z + _r.z, size * 0.32, size * 0.9, 31 + sd, 0.9, _q); } // horns
          if (Math.random() < 0.5) for (const sd of [-1, 1]) { _v.set(sd * size * 0.17, size * 0.05, 0).applyQuaternion(camQ); this.fx.stars.add(_p.x + _v.x + _f.x * 0.3, _p.y + _v.y, _p.z + _v.z + _f.z * 0.3, 0.35, 0.06, 0xfff2a0, 2); } // eyes
        } else put(_p.x, _p.y - size * 0.4, _p.z, size * 0.9, size * 1.05, k * 3.3 + 1, 0.85 * taper + 0.1);
      }
      if (s <= 1) {
        this.wyrmPoint(W, Math.min(1, s), _p);
        for (let k = 0; k < 3; k++) this.fx.particles.emitDir(_p.x, _p.y, _p.z, (Math.random() - 0.5) * 4, Math.random() * 3, (Math.random() - 0.5) * 4, Math.random() < 0.5 ? AMBER : ORANGE, 0.3 + Math.random() * 0.3, 0.45, 3);
      }
    }
    this.cards.count = n; this.cards.instanceMatrix.needsUpdate = true; this.cardAttr.needsUpdate = true;
  }

  /** a point on a wyrm's path at s (0 mouth → 1 impact): rises to an arc, weaves side to side, dives into the ground */
  private wyrmPoint(W: WyrmFx, s: number, out: THREE.Vector3): THREE.Vector3 {
    const dx = W.x1 - W.x0, dz = W.z1 - W.z0, L = Math.hypot(dx, dz) || 1, sx = -dz / L, sz = dx / L;
    const weave = Math.sin(s * Math.PI * 2.5) * 1.6 * W.side * (1 - s * 0.6), gy = this.groundAt(W.x1, W.z1) + 0.2;
    const arc = Math.sin(Math.min(1, s) * Math.PI) * (3.6 + L * 0.25), base = W.y0 + (gy - W.y0) * s * s; // rises high, then dives
    return out.set(W.x0 + dx * s + sx * weave, base + arc * (1 - s * 0.35), W.z0 + dz * s + sz * weave);
  }

  private rigMouth(rig: HeroRig): void { rig.root.updateMatrixWorld(true); rig.head.localToWorld(this.mouth.set(0, -0.13, 0.25)); }

  /** the authored hero's mouth: just below the middle of the eyes (the dark eye vertices of the skinned body near the head,
   *  mapped into the head bone's space through its inverse bind matrix, as Twin Gaze finds them), so it follows every head
   *  turn. False = not found (fallback: the procedural rig's head). */
  private findMouth(h: GltfHero): boolean {
    this.mouthHero = h; this.mouthLocal = null; this.headBone = null; this.neckBone = null;
    const meshes: THREE.SkinnedMesh[] = [];
    h.root.traverse((o) => {
      const sm = o as THREE.SkinnedMesh;
      if ((o as THREE.Bone).isBone && o.name === 'neck') this.neckBone = o as THREE.Bone;
      if (sm.isSkinnedMesh && !h.outlineMeshes.includes(sm) && sm.geometry.attributes.color && sm.skeleton.bones.some((b) => b.name === 'head')) meshes.push(sm);
    });
    const sum = new THREE.Vector3(); let cnt = 0, minY = Infinity;
    for (const sm of meshes) {
      const bi = sm.skeleton.bones.findIndex((b) => b.name === 'head');
      this.headBone = sm.skeleton.bones[bi];
      const pos = sm.geometry.attributes.position, col = sm.geometry.attributes.color, inv = sm.skeleton.boneInverses[bi];
      for (let i = 0; i < pos.count; i++) {
        const r = col.getX(i), b = col.getZ(i);
        if (!(b > r * 3 && b < 0.32 && r < 0.12)) continue;
        _v.fromBufferAttribute(pos, i).applyMatrix4(inv);
        if (_v.length() > 0.25 || _v.y < 0.03) continue;
        sum.add(_v); cnt++; minY = Math.min(minY, _v.y);
      }
    }
    if (!this.headBone || cnt < 6) return false;
    const eyes = sum.divideScalar(cnt);
    // below the eyes by about a third of the eye-to-neck height, nudged out to the face surface
    this.mouthLocal = new THREE.Vector3(eyes.x * 0.2, Math.max(0.0, eyes.y - Math.max(0.055, (eyes.y - minY) + 0.05)), eyes.z * 1.08);
    return true;
  }

  /** turn the neck and head toward the jet while running (k = weight); the previous frame's twist is undone first */
  private headTwist(gltf: GltfHero | null, k: number): void {
    const bones = [this.neckBone, this.headBone];
    if (!gltf || !gltf.ready || !this.headBone) { this.twisted = false; return; }
    if (this.twisted) for (let i = 0; i < 2; i++) { const b = bones[i]; if (b && b.quaternion.equals(this.twistOut[i])) b.quaternion.copy(this.twistBase[i]); }
    this.twisted = false;
    if (k <= 0.01) return;
    let rel = this.aimYaw - gltf.root.rotation.y; rel = Math.atan2(Math.sin(rel), Math.cos(rel));
    rel = Math.max(-1.1, Math.min(1.1, rel)) * k;
    if (Math.abs(rel) < 1e-3) return;
    gltf.root.updateMatrixWorld(true);
    for (let i = 0; i < 2; i++) {
      const b = bones[i]; if (!b || !b.parent) continue;
      this.twistBase[i].copy(b.quaternion);
      b.parent.getWorldQuaternion(_pq);
      _a.copy(_up).applyQuaternion(_pq.invert()).normalize();
      b.quaternion.premultiply(_q.setFromAxisAngle(_a, rel * (i ? 0.55 : 0.45)));
      this.twistOut[i].copy(b.quaternion);
      b.updateMatrixWorld(true);
    }
    this.twisted = true;
  }

  /** ground glow: the jet's firelight, ring fronts, lingering circles, the ember glow under burning foes */
  glowPools(g: GroundGlow, sim: Sim): void {
    if (this.on > 0.05) {
      const hx = this.mouth.x, hz = this.mouth.z, gy = this.groundAt(this.endP.x, this.endP.z);
      g.strip(hx, hz, this.endP.x, this.endP.z, gy + 0.6, Math.max(0.6, Math.tan(this.half) * this.range * 0.55), 0xff7a20, 1.1 * this.on, 1.3);
    }
    for (const R of this.ringFx) {
      const d = Math.min(R.r, 0.6 + R.speed * R.t), k = Math.max(0, 1 - Math.max(0, R.t - (R.r - 0.6) / R.speed) / 0.4), M = Math.min(28, Math.max(8, Math.round(d * 3)));
      for (let i = 0; i < M; i++) { const a0 = i / M * Math.PI * 2, a1 = (i + 1) / M * Math.PI * 2; g.strip(R.x + Math.cos(a0) * d, R.z + Math.sin(a0) * d, R.x + Math.cos(a1) * d, R.z + Math.sin(a1) * d, this.groundAt(R.x, R.z) + 0.5, 0.75, 0xff6a14, 1.1 * k, 1, 0.7); }
    }
    for (const L of this.lingerFx) {
      const k = Math.min(1, L.t / 0.25) * Math.min(1, (L.dur - L.t) / 0.5), M = Math.min(24, Math.max(8, Math.round(L.r * 2.5)));
      for (let i = 0; i < M; i++) { const a0 = i / M * Math.PI * 2, a1 = (i + 1) / M * Math.PI * 2; g.strip(L.x + Math.cos(a0) * L.r, L.z + Math.sin(a0) * L.r, L.x + Math.cos(a1) * L.r, L.z + Math.sin(a1) * L.r, this.groundAt(L.x, L.z) + 0.5, L.w * 0.55, 0xff4a10, 0.7 * k, 1, 0.6); }
    }
    let n = 0;
    for (const e of sim.enemies) { if (!e.alive || e.burnT <= 0) continue; if (n++ > 28) break; g.blob(e.x, this.groundAt(e.x, e.z) + 0.6, e.z, e.def.radius * e.scale * 1.7, 0xff5a14, 0.55 * Math.min(1, e.burnT / 0.35), 0.6); }
  }

  clear(): void {
    this.on = this.pose = this.grow = 0; this.active = false; this.cards.count = 0;
    for (const c of this.cones) c.visible = false;
    this.ringFx.length = 0; this.lingerFx.length = 0; this.wyrmFx.length = 0;
  }
}

const _m2 = new THREE.Matrix4(), _rv = new THREE.Vector3();
/** sideways offset s (share of the jet) perpendicular to the flat direction f */
function _rr(s: number, f: THREE.Vector3): THREE.Vector3 { const l = Math.hypot(f.x, f.z) || 1; return _rv.set(-f.z / l * s, 0, f.x / l * s); }
